import {Link} from '@primer/react'
import {Dialog, type DialogHeaderProps} from '@primer/react/experimental'
import type {SignatureResult, VerificationStatus} from './signed-commit-types'
import SignedCommitHeader from './components/SignedCommitHeader'
import {VerifiedIcon, UnverifiedIcon} from '@primer/octicons-react'
import SignedCommitFooter, {type Signer} from './components/SignedCommitFooter'
import SmimeCommitFooter from './components/SmimeCommitFooter'

import styles from './SignedCommitDialog.module.css'

interface CheckStatusDialogProps {
  isOpen: boolean
  onDismiss: () => void
  verificationStatus: VerificationStatus
  signature: SignatureResult
  hasSignature: boolean
  ariaLabel?: string
}

export function SignedCommitDialog(props: CheckStatusDialogProps) {
  const {ariaLabel, hasSignature, isOpen, onDismiss, signature, verificationStatus} = props

  const {footerLabel, footerUrl} = getCommitSigningHelpLink(signature.helpUrl)

  const reason = signature.signatureVerificationReason.toUpperCase()
  const signedByGitHub = signature.signedByGitHub

  const {icon, icon_color} =
    verificationStatus === 'unverified'
      ? {icon: UnverifiedIcon, icon_color: 'var(--fgColor-muted)'}
      : {icon: VerifiedIcon, icon_color: 'var(--fgColor-success)'}

  let signer: Signer | undefined = undefined
  if (verificationStatus !== 'unverified' && !signedByGitHub) {
    signer = {avatar_url: signature.signerAvatarUrl, login: signature.signerLogin}
  }

  const signatureLink = (
    <Link href={footerUrl} data-testid="signature-link" className="Link--inTextBlock">
      {footerLabel}
    </Link>
  )

  return isOpen ? (
    <Dialog
      onClose={onDismiss}
      title={verificationStatus}
      width="small"
      renderHeader={({dialogLabelId, dialogDescriptionId}: DialogHeaderProps) => (
        <div data-testid="signed-commit-dialog" className={styles.dialogHeaderContainer}>
          <Dialog.Title id={dialogLabelId} className="sr-only">
            {ariaLabel ?? verificationStatus}
          </Dialog.Title>
          <SignedCommitHeader
            reason={reason}
            icon={icon}
            iconColor={icon_color}
            id={dialogDescriptionId}
            verificationStatus={verificationStatus}
            isViewer={signature.isViewer}
            hasSignature={hasSignature}
            signatureType={signature.signatureType}
            signedByGitHub={signedByGitHub}
            keyExpired={signature.keyExpired}
            keyRevoked={signature.keyRevoked}
          />
          <Dialog.CloseButton onClose={onDismiss} />
        </div>
      )}
      renderBody={() => {
        return (
          <Dialog.Body className="p-0">
            {signature.signatureType === 'GpgSignature' || signature.signatureType === 'SshSignature' ? (
              <SignedCommitFooter
                keyHex={signature.keyId}
                signatureType={signature.signatureType}
                signer={signer}
                verifiedAt={signature.verifiedAt}
                showPartiallyVerifiedMessage={verificationStatus === 'partially_verified'}
                signingLinkComponent={signatureLink}
                keyExpired={signature.keyExpired}
                keyRevoked={signature.keyRevoked}
              />
            ) : signature.signatureType === 'SmimeSignature' &&
              signature.signatureCertificateSubject &&
              signature.signatureCertificateIssuer ? (
              <SmimeCommitFooter
                issuer={signature.signatureCertificateIssuer}
                subject={signature.signatureCertificateSubject}
                signingLinkComponent={signatureLink}
                verifiedAt={signature.verifiedAt}
              />
            ) : (
              <div className="d-flex">
                <div className={styles.signatureLinkContent}>{signatureLink}</div>
              </div>
            )}
          </Dialog.Body>
        )
      }}
      className="overflow-y-auto bgColor-default"
    />
  ) : null
}

interface SignedCommitErrorDialogProps {
  isOpen: boolean
  onDismiss: () => void
  helpUrl?: string
  ariaLabel?: string
}

export function SignedCommitErrorDialog({isOpen, onDismiss, helpUrl, ariaLabel}: SignedCommitErrorDialogProps) {
  if (!isOpen) return null

  const helpLink = helpUrl ? getCommitSigningHelpLink(helpUrl) : undefined

  return (
    <Dialog
      onClose={onDismiss}
      title="Verification error"
      width="small"
      renderHeader={({dialogLabelId}: DialogHeaderProps) => (
        <div data-testid="signed-commit-dialog" className={styles.dialogHeaderContainer}>
          <Dialog.Title id={dialogLabelId} className="sr-only">
            {ariaLabel ?? 'Verification error'}
          </Dialog.Title>
          <div data-testid="signed-commit-error-header" className="d-flex p-2 flex-1">
            <span>Verification error</span>
          </div>
          <Dialog.CloseButton onClose={onDismiss} />
        </div>
      )}
      renderBody={props => {
        const {dialogDescriptionId} = props as DialogHeaderProps
        return (
          <Dialog.Body className="p-0">
            <div className={styles.signatureLinkContent}>
              <div id={dialogDescriptionId}>Verification status not available</div>
              {helpLink && (
                <div className="mt-2">
                  <Link href={helpLink.footerUrl} data-testid="signature-link" className="Link--inTextBlock">
                    {helpLink.footerLabel}
                  </Link>
                </div>
              )}
            </div>
          </Dialog.Body>
        )
      }}
      className="overflow-y-auto bgColor-default"
    />
  )
}

function getCommitSigningHelpLink(helpUrl: string) {
  return {
    footerLabel: 'Learn about vigilant mode',
    footerUrl: `${helpUrl}/github/authenticating-to-github/displaying-verification-statuses-for-all-of-your-commits`,
  }
}
